import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { teamService } from '../../services/teamService';
import PointsDashboard from './PointsDashboard';

interface PointsPageProps {
  refreshTrigger?: number;
}

const PointsPage: React.FC<PointsPageProps> = ({ refreshTrigger }) => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      loadTeams();
    }
  }, [user, refreshTrigger]);

  const loadTeams = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');
    
    try {
      let userTeams: any[];
      if (user.role === 'coach' || user.role === 'headCoach') {
        // Same as TeamsPage/App: include teams where user is head coach OR a member (so coaches who are members see leaderboard)
        const [coachTeams, memberTeams] = await Promise.all([
          teamService.getTeamsByCoach(user.id),
          teamService.getTeamsByAthlete(user.id)
        ]);
        const allTeams = [...coachTeams];
        memberTeams.forEach((memberTeam: any) => {
          if (!allTeams.find((t: any) => t.id === memberTeam.id)) {
            allTeams.push(memberTeam);
          }
        });
        userTeams = allTeams;
      } else if (user.role === 'athlete') {
        userTeams = await teamService.getTeamsByAthlete(user.id);
      } else {
        userTeams = [];
      }
      setTeams(userTeams);
    } catch (err: any) {
      setError(err.message || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (teams.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">
          <Typography variant="h6" gutterBottom>
            No Teams Found
          </Typography>
          <Typography variant="body2">
            You need to be part of a team to view points. Please join a team or create one if you're a coach.
          </Typography>
        </Alert>
      </Container>
    );
  }

  // For now, show points for the first team
  // In the future, we could add team selection
  const teamId = teams[0].id;

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Level Streak and Leaderboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your Level Streak and Points
        </Typography>
      </Box>
      
      <PointsDashboard teamId={teamId} refreshTrigger={refreshTrigger} />
    </Container>
  );
};

export default PointsPage;
